import { useEffect, useRef, useState } from 'react';

import type { RegistryOrderCreateRequest, RegistryOrderDetail } from '@/types/registry-documents';

interface UseOrderSubmitOptions {
  opened: boolean;
  onSubmit: (payload: RegistryOrderCreateRequest) => Promise<RegistryOrderDetail>;
  onClose: () => void;
  onOrdered?: (order: RegistryOrderDetail) => void;
}

/**
 * Sending the order, and what came back.
 *
 * A submit can outlive the modal: the call runs for up to two minutes and
 * the parent may close the modal meanwhile. Each close starts a new
 * session, and an answer from an earlier session is dropped — it must
 * neither close the modal a later session is composing in, nor show its
 * error against a different selection.
 */
export function useOrderSubmit({ opened, onSubmit, onClose, onOrdered }: UseOrderSubmitOptions) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const sessionRef = useRef(0);

  useEffect(() => {
    if (!opened) {
      sessionRef.current += 1;
      setSubmitting(false);
      setError(null);
    }
  }, [opened]);

  const submit = async (payload: RegistryOrderCreateRequest) => {
    const session = sessionRef.current;
    const isCurrent = () => sessionRef.current === session;
    setSubmitting(true);
    setError(null);
    try {
      const order = await onSubmit(payload);
      if (isCurrent()) {
        onClose();
        onOrdered?.(order);
      }
    } catch (err) {
      // Kept open on failure: the selection is worth more than a tidy close,
      // and a validation failure means the selection itself needs changing.
      if (isCurrent()) {
        setError(err instanceof Error ? err : new Error('Could not prepare the order'));
      }
    } finally {
      if (isCurrent()) {
        setSubmitting(false);
      }
    }
  };

  return { submitting, error, submit, clearError: () => setError(null) };
}
